import { PrembidLocalStorage } from '@/lib/utils/PrembidLocalStorage';
import { Box, Button, Card, Checkbox, CircularProgress, Container, Divider, Grid, InputAdornment, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import React, { SetStateAction, useCallback, useEffect, useState, useRef } from 'react';
import { connect, } from 'react-redux';
import { clearMyActivityWishlistStatus, handleRetrieveAllLotWishlist, handleRetrieveAllMyBiddingActivity, clearApiData, setFilters } from './store/MyActivity';
import { clearResponses } from '../Lots/store/Lots';
import LotDetailCard from '@/components/Cards/LotDetailCard';
import { handleAuctionByIdFetch } from '../Auctions/store/Auctions';
import ns from '@/lib/utils/NotificationService';
import useHistory from '../../lib/hooks/useHistory';
import { useStickyOffsets } from '../../lib/hooks/useStickyOffsets';
import useStyles from '../MyActivity/MyActivityStyles';
import { isArray } from 'lodash';
import { useSignalRConnectionStatus } from 'prembid-shared-library-npm/signalr';
import { Pagination } from '../../components/composite/Pagination';
import { FaSearch, FaUndo } from 'react-icons/fa';
import StickyBox from "react-sticky-box";
import { changeProfileId } from '../../components/LoginForm/store/LoginForm';
import { CompactLotCard } from '../../components/library/CompactLotCard';
import { ViewModeToggle, View } from '../../components/library/ViewModeToggle';

interface Props {
    handleLotWishlistDataFetch_d: (profileId: string, filters: any, pagination?: any, onCompletedCallback?: (res: any) => void) => void,
    handleMyBiddingActivityDataFetch_d: (profileId: string, filters: any, pagination?: any, onCompletedCallback?: (res: any) => void) => void,
    handleAuctionByIdFetch_d: (auctionId: string, onCompletedCallback?: (res: any) => void) => void,
    clearResponses_d: () => void,
    loading: boolean,
    settings: any,
    contact?: any,
    profileId: any,
    error: any,
    errorText: string,
    lotWishlistData: any,
    myBiddingActivityData: any,
    lotWishlistPagination?: any,
    myBiddingActivityPagination?: any,
    onHandleChangePage?: (pagination?: any) => void,
    availableFilters?: any[],
    selectedFilters?: any,
    onFiltersChange?: (filters: any) => void,
    isMyActivityWishlistObject: any,
    clearMyActivityWishlistStatus_d: () => void,
    filters,
    setFilters
}

const MyActivity: React.FC<Props> = props => {
    const {
        handleLotWishlistDataFetch_d,
        handleMyBiddingActivityDataFetch_d,
        settings,
        isMyActivityWishlistObject,
        clearMyActivityWishlistStatus_d,
        myBiddingActivityData,
        lotWishlistData,
        filters,
        setFilters,
        profileId
    } = props;

    const { classes: { compactLotCardsContainer, containerWidth } } = useStyles();
    const history = useHistory();
    const { barRef, barTop } = useStickyOffsets();

    let sortBy: string[] = ["Number: Ascending", "Number: Descending", "Opening Bid: Low to High", "Opening Bid: High to Low", "Time: Remaining"];
    // Same status filter on both tabs, as on mobile.
    let lotType: string[] = ["Open/Upcoming", "Completed"];
    let pageType: string[] = ["My Current Activity", "My Wishlist"];
    let viewType: View[] = [View.DETAIL, View.COMPACT]

    // The detail/compact choice is remembered on this device (mobile remembers it too).
    const rememberedView = (() => {
        try { return localStorage.getItem("myActivity.view") as View | null; } catch { return null; }
    })();

    const initialFilters = {
        lotType: "Open/Upcoming",
        pageType: "My Current Activity",
        viewType: rememberedView && viewType.includes(rememberedView) ? rememberedView : viewType[viewType.indexOf(settings.DefaultLotView ?? View.COMPACT)],
        sortBy: settings.DefaultLotOrdering && settings.DefaultLotOrdering.length > 0
            ? settings.DefaultLotOrdering
            : "Number: Ascending"
    };

    const [myBiddingData, setMyBiddingData] = useState<any>(
        myBiddingActivityData ? {
            ...myBiddingActivityData,
            list: myBiddingActivityData.list.map((x: any) => ({
                ...x,
                lots: {
                    ...x.lots
                }
            }))
        } : null
    );
    const [myWishlistData, setWishlistData] = useState<any>(
        lotWishlistData ? {
            ...lotWishlistData,
            list: lotWishlistData.list?.map((x: any) => ({
                ...x,
                lots: {
                    ...x.lots,
                    isOnWishlist: true
                }
            }))
        } : null
    );

    const [gridLoading, setGridLoading] = useState<boolean>(false);
    const normaliseFilters = (value: any) => value ? { ...value, lotType: value.lotType === "Open" ? "Open/Upcoming" : value.lotType, viewType: rememberedView && viewType.includes(rememberedView) ? rememberedView : value.viewType } : value;
    const [filterFields, setFilterFields] = useState(normaliseFilters(filters) || initialFilters);
    const [selectedTab, setSelectedTab] = useState<number | undefined>(
        filters.pageType === 'My Current Activity' ? 0 : 1
    );

    // "Recent Activity" (the lots the user bid on most recently first) is only offered, and is the default, on the
    // My Current Activity tab with the Completed status.
    const RECENT_ACTIVITY = "Recent Activity";
    const usesRecentActivity = (tab: number | undefined, status: string | undefined) => tab === 0 && status === "Completed";
    const defaultSortFor = (tab: number | undefined, status: string | undefined) => usesRecentActivity(tab, status) ? RECENT_ACTIVITY : initialFilters.sortBy;
    // Moving into or out of Completed My Activity switches to that view's default sort; otherwise the sort is kept.
    const sortAfterChange = (newTab: number | undefined, newStatus: string | undefined) =>
        usesRecentActivity(selectedTab, filterFields?.lotType) !== usesRecentActivity(newTab, newStatus)
            ? defaultSortFor(newTab, newStatus)
            : filterFields?.sortBy;

    const sortOptions = usesRecentActivity(selectedTab, filterFields?.lotType) ? [RECENT_ACTIVITY, ...sortBy] : sortBy;
    const currentSortValue = sortOptions.includes(filterFields?.sortBy) ? filterFields.sortBy : defaultSortFor(selectedTab, filterFields?.lotType);

    useEffect(() => {
        if (!filters || Object.keys(filters).length === 0) {
            setFilters(initialFilters);
            setFilterFields(initialFilters);
        } else {
            const restored = { ...normaliseFilters(filters), sortBy: filters.sortBy ?? initialFilters.sortBy };
            setFilters(restored);
            setFilterFields(restored);
        }
    }, []);

    const updateFilters = useCallback((newFilters: any) => {
        setFilterFields(newFilters);
        setFilters(newFilters);
    }, [setFilters]);

    useEffect(() => {
        if (profileId) {
            if (selectedTab === 0) {
                refreshMyActivity(filters, myBiddingData?.pagination);
            }
            else if (selectedTab === 1) {
                refreshMyWishlist(filters, myWishlistData?.pagination);
            }
        } else if (!PrembidLocalStorage.currentUser?.userId) {
            // Only leave when nobody is logged in; on a fresh page load the profile id arrives a moment later.
            history.replace('/Home');
        }
    }, [profileId]);

    useEffect(() => {
        if (isMyActivityWishlistObject?.lotId !== "") {
            if (selectedTab === 0) {
                const index = myBiddingData?.list?.findIndex((e: any) => e.id === isMyActivityWishlistObject?.lotId);
                if (index > -1) {
                    let temp = { ...myBiddingData }
                    let tempObject = temp.list[index];
                    tempObject.isOnWishlist = !(tempObject?.isOnWishlist);
                    temp.list[index] = tempObject;
                }
            }

            if (selectedTab === 1) {
                const index = myWishlistData?.list?.findIndex((e: any) => e.lots?.id === isMyActivityWishlistObject?.lotId);
                if (index > -1) {
                    let temp = { ...myWishlistData }
                    let tempObject = temp.list[index].lots;
                    if (tempObject.isOnWishlist !== undefined) {
                        tempObject.isOnWishlist = !(tempObject?.isOnWishlist);
                    } else {
                        tempObject.isOnWishlist = false
                    }

                    temp.list[index].lots = tempObject;
                }
            }

            clearMyActivityWishlistStatus_d();
        }

    }, [isMyActivityWishlistObject])

    useEffect(() => {
        if (filterFields?.sortBy === null) {
            setFilterFields(prevFields => ({
                ...prevFields,
                sortBy: initialFilters?.sortBy
            }));
        }
    }, [filterFields?.sortBy]);

    function getLotFeatures(lot: any) {
        let featuresArr: any[] = [];

        if (lot?.dataCaptured?.additionalProperties) {
            lot.dataCaptured.additionalProperties?.map(x => {
                if (x.name !== '' && x.value !== '' && !x.hidden)
                    featuresArr.push({ ...x, key: x.name })
            });

            featuresArr.sort((a, b) => a.order - b.order);
        }

        return featuresArr;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const refreshMyActivity = (filters?: any, pagination?: any) => {
        setGridLoading(true);

        const formattedFilters = {
            ...filters,
            lotType: filters?.lotType?.toLowerCase() === "open/upcoming" ? "open" : filters?.lotType?.toLowerCase(),
        };

        handleMyBiddingActivityDataFetch_d(profileId, formattedFilters, pagination, (res) => {
            if (res.success) {
                const data = res.response;
                setMyBiddingData({
                    ...data,
                    list: data.list.map((x: any) => ({
                        ...x,
                        lots: {
                            ...x.lots
                        }
                    }))
                });
            } else {
                ns.error('', res.error);
            }

            setGridLoading(false);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const refreshMyWishlist = (filters?: any, pagination?: any) => {
        setGridLoading(true);

        const formattedFilters = {
            ...filters,
            lotType: filters?.lotType?.toLowerCase() === "open/upcoming" ? "open" : filters?.lotType?.toLowerCase(),
        };

        handleLotWishlistDataFetch_d(profileId, formattedFilters, pagination, (res) => {
            if (res.success) {
                var data = res.response;
                const newList = data?.list?.map((x: any) => ({
                    ...x,
                    lots: {
                        ...x.lots,
                        isOnWishlist: true
                    }
                }));
                setWishlistData({ ...data, list: newList });
            } else {
                ns.error('', res.error);
            }

            setGridLoading(false);
        });
    };

    const handleWishlistPageChange = (newPagination: any) => {
        // Use the new pagination values passed from the Pagination component
        refreshMyWishlist(filterFields, newPagination);
    }

    const handleMyActivityPageChange = (newPagination: any) => {
        // Use the new pagination values passed from the Pagination component
        refreshMyActivity(filterFields, newPagination);
    }

    // Reload whichever list is showing with the given filters (back to page one, keeping the page size).
    const reloadCurrentList = (newFilters: any, tab = selectedTab) => {
        const current = tab === 0 ? myBiddingData?.pagination : myWishlistData?.pagination;
        const pagination = current ? { currentPage: 1, pageSize: current.pageSize, total: current.total } : undefined;
        if (tab === 0) refreshMyActivity(newFilters, pagination);
        else refreshMyWishlist(newFilters, pagination);
    };

    const handleSelectedFilterClick = (e: any) => {
        const newFilters = { ...filterFields, lotType: e.target.value, sortBy: sortAfterChange(selectedTab, e.target.value) };
        updateFilters(newFilters);
        reloadCurrentList(newFilters);
    };

    // Switching tabs keeps the status and sort (shared by both tabs, as on mobile), except "Recent Activity" (above).
    const handleSelectTab = (index: number) => {
        if (index === selectedTab) return;
        setSelectedTab(index);
        const newFilters = { ...filterFields, pageType: pageType[index], sortBy: sortAfterChange(index, filterFields?.lotType) };
        updateFilters(newFilters);
        reloadCurrentList(newFilters, index);
    };

    const handleSortByValueChanged = (e: any) => {
        const newFilters = { ...filterFields, sortBy: e.target.value };
        updateFilters(newFilters);
        reloadCurrentList(newFilters);
    };

    // Only the layout changes: no reload, nothing else reset.
    const handleSetSelectedView = (value: SetStateAction<View>) => {
        const newViewType = typeof value === "function" ? value(filterFields.viewType) : value;
        try { localStorage.setItem("myActivity.view", newViewType); } catch { }
        updateFilters({ ...filterFields, viewType: newViewType });
    };

    const handleResetFilters = () => {
        const newFilters = { ...filterFields, lotType: initialFilters.lotType, sortBy: initialFilters.sortBy };
        updateFilters(newFilters);
        reloadCurrentList(newFilters);
    };

    const handleApplyClicked = () => {
        reloadCurrentList(filterFields);
    }

    const filtersChanged = filterFields?.lotType !== initialFilters.lotType || currentSortValue !== defaultSortFor(selectedTab, filterFields?.lotType);

    // Back online after a dropped live connection: reload the list being shown (same filters and page), like the
    // Lots and Auctions pages and the mobile app, so bids missed while offline show up.
    useSignalRConnectionStatus({
        onConnect: () => {
            if (!profileId) return;
            if (selectedTab === 0) refreshMyActivity(filterFields, myBiddingData?.pagination);
            else refreshMyWishlist(filterFields, myWishlistData?.pagination);
        },
    });

    const currentPagination = selectedTab === 0 ? myBiddingData?.pagination : myWishlistData?.pagination;

    return <div className="pb-page" style={{ background: settings?.Styles?.OverridePageBackgroundColor || undefined }}>
        <Container maxWidth={false} className={containerWidth}>
            <div className="pb-page-head">
                <div>
                    <h1>My Activity</h1>
                    <p>{settings.LotName}s you are bidding on and your wishlist.</p>
                </div>
            </div>

            <div className="pb-profile-form pb-activity-tabs">
                <ul className="pb-tabs MuiList-root" role="tablist">
                    {pageType.map((label, index) => (
                        <li
                            key={label}
                            role="tab"
                            aria-selected={selectedTab === index}
                            className={"MuiListItem-root" + (selectedTab === index ? " Mui-selected" : "")}
                            onClick={() => handleSelectTab(index)}
                        >
                            {label}
                        </li>
                    ))}
                </ul>
            </div>
        </Container>

        <StickyBox key={"bar-" + barTop} offsetTop={barTop} style={{ zIndex: 23 }} >
            <div ref={barRef} className="pb-activity-toolbar-wrap">
                <Container maxWidth={false} className={containerWidth}>
                    <div className="pb-activity-toolbar">
                        <ViewModeToggle selectedView={filterFields?.viewType} setSelectedView={handleSetSelectedView} />

                        <Select value={filterFields?.lotType} onChange={handleSelectedFilterClick} variant="outlined" className="pb-activity-select" aria-label="Status">
                            {lotType.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                        </Select>

                        <Select value={currentSortValue} onChange={handleSortByValueChanged} variant="outlined" className="pb-activity-select pb-activity-select--wide" aria-label="Sort by">
                            {sortOptions.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                        </Select>

                        {filtersChanged && (
                            <button type="button" className="pb-activity-reset" onClick={handleResetFilters}>
                                <FaUndo size={12} /> Reset
                            </button>
                        )}

                        {currentSortValue === "Time: Remaining" && (
                            <button type="button" className="pb-btn pb-btn-ink pb-activity-reorder" onClick={handleApplyClicked}>
                                Reorder {settings.LotName}s
                            </button>
                        )}

                        <div className="pb-activity-pagination">
                            <Pagination
                                onHandleChangePage={(newPage: any) => {
                                    if (!currentPagination) return;
                                    const newPagination = {
                                        currentPage: typeof newPage === 'object' ? newPage.currentPage : newPage,
                                        pageSize: newPage.pageSize,
                                        total: currentPagination.total
                                    };
                                    if (selectedTab === 0) handleMyActivityPageChange(newPagination);
                                    else handleWishlistPageChange(newPagination);
                                }}
                                pagination={currentPagination}
                                loading={gridLoading}
                            />
                        </div>
                    </div>
                </Container>
            </div>
        </StickyBox>

        <Container maxWidth={false} className={containerWidth}>
            <div className="pb-activity-list" style={{ opacity: gridLoading ? 0.5 : 1 }}>
                {gridLoading && (
                    <div className="pb-activity-loading">
                        <CircularProgress
                            style={{ color: settings?.Styles?.OverrideLoadingSpinnerColor ? settings?.Styles?.OverrideLoadingSpinnerColor : settings?.Styles?.Primary }}
                            size={48}
                        />
                    </div>
                )}

                {(filterFields?.pageType === "My Wishlist"
                    ? (myWishlistData?.pagination?.total || 0) === 0
                    : (myBiddingData?.list?.length || 0) === 0) && !gridLoading && (
                    <div className="pb-empty">{"No " + settings.LotName + "s to be displayed"}</div>
                )}

                {(filterFields?.pageType === "My Wishlist" ? myWishlistData?.list : myBiddingData?.list) && (
                    <>
                        {filterFields?.viewType === View.COMPACT && (
                            <Box className={compactLotCardsContainer}>
                                {(filterFields?.pageType === "My Wishlist" ? myWishlistData?.list : myBiddingData?.list)?.map((item: any) => {
                                    const lot = filterFields?.pageType === "My Wishlist" ? item.lots : item;
                                    return (
                                        <CompactLotCard
                                            key={lot.id}
                                            lot={lot}
                                            startDateTimeAt={lot?.startDateTimeAt}
                                            hasExtensions={lot?.lotBidExtensionCount > 0}
                                            commission={lot?.commission > 0 ? lot?.commission?.toFixed(2) : 0}
                                            lotFeatures={getLotFeatures(lot)}
                                            auctionType={lot?.auctionType}
                                            overrideGuidePriceRulesAndAlwaysHide={false}
                                            registrationStatus={() => { }}
                                            overrideShowBidButtonForCompletedLots={false}
                                            auctionId={lot?.auctionId}
                                            wishListStatus={lot?.isOnWishlist ?? false}
                                            variant={""}
                                            highestBid={lot?.winningRegisteredProfileAmount}
                                            highestBidProfileId={lot?.winningProfileId}
                                            settings={settings}
                                            extendedBy={lot?.extendedBy}
                                        />
                                    );
                                })}
                            </Box>
                        )}

                        {filterFields?.viewType === View.DETAIL && (
                            <Grid container spacing={3}>
                                {(filterFields?.pageType === "My Wishlist" ? myWishlistData?.list : myBiddingData?.list)?.map((item: any) => {
                                    const lot = filterFields?.pageType === "My Wishlist" ? item.lots : item;
                                    return (
                                        <LotDetailCard
                                            key={lot?.id}
                                            imgSrc={lot?.imageUrl}
                                            displayFullLengthImage={true}
                                            header={lot?.name}
                                            description={lot?.description}
                                            auctionId={lot?.auctionId}
                                            auctionType={lot?.auctionType}
                                            lotId={lot?.id}
                                            lotNumber={lot?.number}
                                            startDateTimeAt={lot?.startDateTimeAt}
                                            endDateTimeAt={lot?.endDateTimeAt}
                                            successBadgeText={lot?.stcStatus?.toString()}
                                            startingPrice={lot?.startingPrice}
                                            guidePrice={lot?.guidePrice?.toString()}
                                            dangerBadgeText={lot?.number}
                                            lotFeatures={getLotFeatures(lot)}
                                            hasExtensions={lot?.lotBidExtensionCount > 0}
                                            isActive={lot?.isActive}
                                            commission={lot?.commission > 0 ? lot?.commission?.toFixed(2) : 0}
                                            registrationStatus={() => { }}
                                            documentCount={(PrembidLocalStorage?.currentUser && profileId) ? (lot?.publicDocumentCount + lot?.privateDocumentCount) : lot?.publicDocumentCount}
                                            wishListStatus={lot?.isOnWishlist ?? false}
                                            overrideGuidePriceRulesAndAlwaysHide={true}
                                            overrideShowBidButtonForCompletedLots={filterFields?.lotType === "Completed"}
                                            showReserve={lot?.showReserve}
                                            highestBid={lot?.winningRegisteredProfileAmount}
                                            highestBidProfileId={lot?.winningProfileId}
                                            extendedBy={lot?.extendedBy}
                                            lot={lot}
                                        />
                                    );
                                })}
                            </Grid>
                        )}
                    </>
                )}
            </div>
        </Container>
    </div>
}

const mapStateToProps = (state: any) => ({
    lotWishlistData: state.myActivity.lotWishlistData,
    myBiddingActivityData: state.myActivity.myBiddingActivityData,
    filters: state.myActivity.filters,
    error: state.myActivity.error,
    errorText: state.myActivity.errorText,
    success: state.myActivity.success,
    loading: state.progressSpinner.loading,
    settings: state.settings.settings,
    registrationStatus: state.participateButton.registrationStatus,
    profileId: state.loginForm.changeProfileId,
    isMyActivityWishlistObject: state.myActivity.isMyActivityWishlistObject,
})

const mapDispatchToProps = (dispatch: any) => {
    return {
        setFilters: (filters: any) => dispatch(setFilters(filters)),
        handleLotWishlistDataFetch_d: (profileId: string, filters?: any, pagination?: any, onCompletedCallback?: (res: any) => void) => dispatch(handleRetrieveAllLotWishlist(profileId, filters, pagination, onCompletedCallback)),
        handleMyBiddingActivityDataFetch_d: (profileId: string, filters?: any, pagination?: any, onCompletedCallback?: (res: any) => void) => dispatch(handleRetrieveAllMyBiddingActivity(profileId, filters, pagination, onCompletedCallback)),
        handleAuctionByIdFetch_d: (auctionId: string, onCompletedCallback?: (res: any) => void) => dispatch(handleAuctionByIdFetch(auctionId, onCompletedCallback)),
        clearResponses_d: () => dispatch(clearResponses()),
        clearMyActivityWishlistStatus_d: () => dispatch(clearMyActivityWishlistStatus()),

    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MyActivity);